import { NavBar } from 'components/NavBar/NavBar'
import { setConfirmModal } from 'features/global/globalSlice'
import React, { useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { AppDispatch, RootState } from 'store'
import { Button, Flex, MainHeadline, PageFooter, PageWrapper } from 'styling/GlobalStyles'
import { Pagination } from 'components/Pagination/Pagination'
import { formatNumbersForDisplay, getParams, handlePagination } from 'utils/helpers'
import { ModalTypes } from 'types/global.interface'
import { NoItems } from 'components/NoItems-fallback/NoItems'
import useQueryPagination from 'hooks/useQueryPagination'
import { TanksModal } from './TanksModal'
import { clearTanks, deleteFuelTank, getFuelTanks } from 'features/fuelManagement/fuelManagementSlice'
import {
    PctBadge,
    StatusPill,
    TankCard,
    TankFill,
    TankInfo,
    TankMetricBlock,
    TankMetricLabel,
    TankMetricRow,
    TankMetricValue,
    TankName,
    TanksGrid,
    TankTopRow,
    TankVisual,
} from './FuelTanks.styled'

const STATUS_COLORS = {
    ok: '#22C55E',
    watch: '#F97316',
    low: '#EF4444',
}

const getTankStatus = (current: number, capacity: number): 'ok' | 'watch' | 'low' => {
    const pct = capacity > 0 ? (current / capacity) * 100 : 0
    if (pct > 50) return 'ok'
    if (pct > 20) return 'watch'
    return 'low'
}

const STATUS_LABELS = { ok: 'Ok', watch: 'Watch', low: 'Low' }

export const FuelTanks = () => {
    const [modal, setModal] = useState({ open: false, value: null })
    const dispatch = useDispatch<AppDispatch>()
    const [params, setParams] = useState<any>(getParams({ location }))
    const { tanks } = useSelector((store: RootState) => store.fuelManagement)

    useQueryPagination({ fetchFunctions: [getFuelTanks], cleanUpFunctions: [clearTanks], params })

    const handleRemove = (id: number) => {
        dispatch(
            setConfirmModal({
                isOpen: true,
                onSave: async () => {
                    await dispatch(deleteFuelTank(id))
                },
                setParams: {
                    type: ModalTypes.Delete,
                    f: setParams,
                    noItems: tanks.data.length < 2,
                },
                word: 'delete tank',
            }),
        )
    }

    return (
        <>
            <PageWrapper>
                <NavBar defaultValue={params.q} input placeholder='Search Fuel Tanks...' setParams={setParams} />
                <Flex between mb='16'>
                    <MainHeadline>Fuel Tanks</MainHeadline>
                    <Flex gap='0.75rem'>
                        <Button orange onClick={() => setModal({ open: true, value: null })}>Add Tank</Button>
                    </Flex>
                </Flex>

                {!tanks?.data?.length ? (
                    <NoItems title='Fuel Tanks' />
                ) : (
                    <TanksGrid>
                        {tanks.data.map((item) => {
                            const pct = item.capacity > 0
                                ? Math.round((item.currentAmount / item.capacity) * 100)
                                : 0
                            const status = getTankStatus(item.currentAmount, item.capacity)
                            const color = STATUS_COLORS[status]

                            return (
                                <TankCard key={item.id} onClick={() => setModal({ open: true, value: item })} style={{ cursor: 'pointer' }}>
                                    <TankVisual>
                                        <TankFill h={pct} color={color} />
                                        <PctBadge h={pct} color={color}>{pct}%</PctBadge>
                                    </TankVisual>

                                    <TankInfo>
                                        <TankTopRow>
                                            <TankName>{item.name}</TankName>
                                            <StatusPill tone={status}>{STATUS_LABELS[status]}</StatusPill>
                                        </TankTopRow>

                                        <div>
                                            <TankMetricLabel>Current Amount</TankMetricLabel>
                                            <TankMetricValue>{formatNumbersForDisplay(item.currentAmount)}l</TankMetricValue>
                                        </div>

                                        <TankMetricRow>
                                            <TankMetricBlock>
                                                <TankMetricLabel>Capacity</TankMetricLabel>
                                                <TankMetricValue>{formatNumbersForDisplay(item.capacity)} L</TankMetricValue>
                                            </TankMetricBlock>
                                        </TankMetricRow>
                                    </TankInfo>
                                </TankCard>
                            )
                        })}
                    </TanksGrid>
                )}
            </PageWrapper>

            {modal.open && (
                <TanksModal value={modal.value} closeModal={() => setModal({ value: null, open: false })} />
            )}

            <PageFooter>
                <Pagination
                    setPage={(page) => handlePagination(page, setParams)}
                    pageCount={Math.ceil(tanks.totalPages)}
                    forcePage={(params.page ? params.page : 1) - 1}
                />
            </PageFooter>
        </>
    )
}
